/** DynamoDB / Events API: independent lifecycle flags (latest row wins). */

import type { DeviceLifecycleSnapshot } from './types/HelperTypes';

export type { DeviceLifecycleSnapshot };

function coerceBool(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (typeof v === 'string' && ['1', 'true', 'yes', 'on'].includes(v.trim().toLowerCase())) return true;
  return false;
}

/** Normalize API / Dynamo row (new BOOL fields or legacy `lifecycleStatus`). */
export function normalizeLifecycleSnapshot(input: unknown): DeviceLifecycleSnapshot {
  if (input == null || typeof input !== 'object') return {};
  const row = input as Record<string, unknown>;
  const hasFlags =
    'lost' in row || 'decommissioned' in row || 'damaged' in row || 'errored' in row;
  if (hasFlags) {
    return {
      lost: coerceBool(row.lost),
      decommissioned: coerceBool(row.decommissioned),
      damaged: coerceBool(row.damaged),
      errored: coerceBool(row.errored),
      reason: typeof row.reason === 'string' ? row.reason : undefined,
    };
  }
  const st = String(row.lifecycleStatus ?? '')
    .trim()
    .toUpperCase();
  if (st === 'LOST') return { lost: true, reason: typeof row.reason === 'string' ? row.reason : undefined };
  if (st === 'DECOMMISSIONED') {
    return { decommissioned: true, reason: typeof row.reason === 'string' ? row.reason : undefined };
  }
  if (st === 'DAMAGED') return { damaged: true, reason: typeof row.reason === 'string' ? row.reason : undefined };
  return {
    reason: typeof row.reason === 'string' ? row.reason : undefined,
  };
}

/** True when no out-of-service flags are set (missing row = in service). */
export function isInServiceLifecycle(input: unknown): boolean {
  if (typeof input === 'string') {
    const s = input.trim().toUpperCase() || 'ACTIVE';
    return s === 'ACTIVE';
  }
  const n = normalizeLifecycleSnapshot(input);
  return !n.lost && !n.decommissioned && !n.damaged && !n.errored;
}

export function isOutOfServiceLifecycle(input: unknown): boolean {
  return !isInServiceLifecycle(input);
}

/** Short labels for tables (e.g. "Lost, Errored" or "In service"). */
export function formatLifecycleSummary(input: unknown): string {
  const n = normalizeLifecycleSnapshot(input);
  const parts: string[] = [];
  if (n.lost) parts.push('Lost');
  if (n.decommissioned) parts.push('Decommissioned');
  if (n.damaged) parts.push('Damaged');
  if (n.errored) parts.push('Errored');
  if (parts.length === 0) return 'In service';
  return parts.join(', ');
}

export function lifecycleFlagYesNo(v: boolean | undefined): string {
  return v ? 'Y' : '—';
}
